/**
 * Toca um "ding" via Web Audio API — sem arquivo de áudio embutido.
 *
 * Som: 2 senoides (760Hz + 1200Hz) com decay prolongado. Total ~500ms.
 * Pico de volume 0.8 — o dono escolheu som e volume testando no playground
 * (o original era ~180ms a 0.4 e passava despercebido: "muito baixo").
 *
 * Chrome bloqueia AudioContext até receber primeiro user gesture (click/keydown).
 * Pra contornar, registramos um listener `pointerdown` global que faz `resume()`
 * UMA vez. Depois disso o ctx fica em estado 'running' permanentemente.
 */

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  // biome-ignore lint/suspicious/noExplicitAny: webkitAudioContext em Safari antigo
  const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Tenta destravar o AudioContext em qualquer click/key da página. Idempotente.
 * Chamar 1x do hook de notificações no mount.
 */
export function primeAudioUnlock(): void {
  if (unlocked || typeof window === 'undefined') return;
  const handler = (): void => {
    if (unlocked) return;
    const audioCtx = getCtx();
    if (!audioCtx) return;
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume().catch(() => {});
    }
    unlocked = true;
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
    // biome-ignore lint/suspicious/noConsole: debug útil
    console.debug('[notifications] AudioContext desbloqueado pelo primeiro user gesture');
  };
  window.addEventListener('pointerdown', handler);
  window.addEventListener('keydown', handler);
}

/** Pico de volume dos toques (0..1). Escolhido pelo dono: 80%. */
const PEAK = 0.8;

function tone(audioCtx: AudioContext, freq: number, startAt: number, durationMs: number): void {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;

  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(PEAK, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationMs / 1000);

  osc.connect(gain).connect(audioCtx.destination);
  osc.start(startAt);
  osc.stop(startAt + durationMs / 1000);
}

export function playNotificationSound(): void {
  const audioCtx = getCtx();
  if (!audioCtx) {
    // biome-ignore lint/suspicious/noConsole: debug
    console.debug('[notifications] som: AudioContext indisponível');
    return;
  }
  if (audioCtx.state === 'suspended') {
    // biome-ignore lint/suspicious/noConsole: debug
    console.debug(
      '[notifications] som: AudioContext suspended — tentando resume (clica em qualquer lugar pra destravar)',
    );
    void audioCtx.resume().catch(() => {});
    // Se ainda suspenso após resume async, sai — não toca silencioso.
    if (audioCtx.state === 'suspended') return;
  }
  const now = audioCtx.currentTime;
  // "Ding prolongado": mesmas notas do ding original, decay 3x mais longo —
  // mais presente sem ficar mais agudo.
  tone(audioCtx, 760, now, 300);
  tone(audioCtx, 1200, now + 0.08, 420);
}

/**
 * Som do CHAT INTERNO — deliberadamente DIFERENTE do de cliente (pedido do
 * dono: "som diferente para diferenciar"). Cliente = dupla ASCENDENTE
 * (760→1200Hz); interno = dupla DESCENDENTE mais grave (980→620Hz), lê como
 * "pop-pop" de mensagem de colega, não de atendimento.
 */
export function playInternalChatSound(): void {
  const audioCtx = getCtx();
  if (!audioCtx) return;
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume().catch(() => {});
    if (audioCtx.state === 'suspended') return;
  }
  const now = audioCtx.currentTime;
  tone(audioCtx, 980, now, 90);
  tone(audioCtx, 620, now + 0.09, 140);
}
